import React from "react";
import {
  getIntegerGetter,
  getInvestmentScoreHeader,
  renderIndexValue,
} from "@client.components/DataTable/utils";
import GridHeader from "@client.components/GridHeader";
import RecurringTypes from "@client.enums/recurringTypes";
import { formatDate, getDifferenceInMinutes } from "@client.utils/dateUtils";
import MeetingNameCell from "./MeetingNameCell";
import { DateFormat } from "@client.enums/dateTypes";

const getDurationFormat = ({ data, value }, t) => {
  const diff = getDifferenceInMinutes(value, data.endDate);

  return t("components.Reports.MeetingsDataTable.dateCellFormat", {
    from: formatDate(value, TimeFormat),
    to: formatDate(data.endDate, TimeFormat),
    diff,
  });
};

const RECURRING_MAP = {
  [RecurringTypes.All]: "all",
  [RecurringTypes.BiYearly]: "biYearly",
  [RecurringTypes.Yearly]: "yearly",
  [RecurringTypes.Quaterly]: "quaterly",
  [RecurringTypes.Monthly]: "monthly",
  [RecurringTypes.BiWeekly]: "biWeekly",
  [RecurringTypes.Weekly]: "weekly",
  [RecurringTypes.Daily]: "daily",
};

export const getRecurringFormat = ({ value }, t) => {
  return t(
    value === null
      ? "components.Settings.InsightsSettings.RecurringTypes.none"
      : `components.Settings.InsightsSettings.RecurringTypes.${RECURRING_MAP[value]}`
  );
};
export const getDateFormat = ({ value }) => formatDate(value, DateFormat);

const getAcceptanceFormat = ({ value }, t) => {
  return t(`components.Reports.MeetingsDataTable.acceptanceFormat`, { value });
};

const getCostIndexGetter = ({ data }, isCostIndexPresentation) => {
  return getIntegerGetter(
    data,
    isCostIndexPresentation ? "costIndex" : "avgPrice",
    isCostIndexPresentation
  );
};

const TimeFormat = "HH:mm";

// Function to highlight meetings based on highlights object from server
const getHighlightingClasses = (fieldName) => {
  return {
    "highlighted-meeting": (params) => {
      return params.data.highlights && params.data.highlights[fieldName] === true;
    }
  };
};

export const getMeetingsColumns = (
  t,
  data,
  isCostIndexPresentation,
) => [
  {
    field: "name",
    tooltipField: "name",
    headerName: t("components.Reports.MeetingsDataTable.name"),
    headerComponent: GridHeader,
    cellClassRules: getHighlightingClasses("name"),
    cellRenderer: (props) => {
      return (
        <MeetingNameCell
          rowData={props.data}
          meetings={
            props.data.recurringId === null
              ? []
              : data.filter((d) => d.recurringId === props.data.recurringId)
          }
        />
      );
    },
  },
  {
    field: "startDate",
    headerName: t("components.Reports.MeetingsDataTable.date"),
    headerClass: "data-table-header header-center",
    cellClass: "data-table-cell cell-center",
    headerComponent: GridHeader,
    cellRenderer: getDateFormat,
    tooltipValueGetter: getDateFormat,
  },
  {
    field: "startDate",
    headerName: t("components.Reports.MeetingsDataTable.time"),
    headerClass: "data-table-header header-center",
    cellClass: "data-table-cell cell-center",
    headerComponent: GridHeader,
    cellRenderer: ({ data, value }) => getDurationFormat({ data, value }, t),
    tooltipValueGetter: ({ data, value }) =>
      getDurationFormat({ data, value }, t),
  },
  {
    field: "recurringType",
    headerName: t("components.Reports.MeetingsDataTable.recurring"),
    headerClass: "data-table-header header-center",
    cellClass: "data-table-cell cell-center",
    headerComponent: GridHeader,
    cellClassRules: getHighlightingClasses("recurringType"),
    cellRenderer: ({ value }) => getRecurringFormat({ value }, t),
    tooltipValueGetter: ({ value }) => getRecurringFormat({ value }, t),
  },
  {
    field: "pastMeetingsQty",
    tooltipField: "pastMeetingsQty",
    headerName: t("components.Reports.MeetingsDataTable.pastMettings"),
    headerClass: "data-table-header header-center",
    cellClass: "data-table-cell cell-center",
    headerComponent: GridHeader,
  },
  {
    field: "attendeesQty",
    tooltipField: "attendeesQty",
    headerName: t("components.Reports.MeetingsDataTable.participants"),
    cellClassRules: getHighlightingClasses("attendeesQty"),
    headerClass: "data-table-header header-center",
    cellClass: "data-table-cell cell-center",
    headerComponent: GridHeader,
  },
  {
    field: "acceptancePercentage",
    headerName: t("components.Reports.MeetingsDataTable.acceptance"),
    headerClass: "data-table-header header-center",
    cellClass: "data-table-cell cell-center",
    headerComponent: GridHeader,
    cellRenderer: ({ value }) => getAcceptanceFormat({ value }, t),
    tooltipValueGetter: ({ value }) => getAcceptanceFormat({ value }, t),
  },
  {
    field: "costValue",
    headerClass: "data-table-header header-center",
    cellClass: "data-table-cell cell-cost-index cell-center",
    maxWidth: 250,
    headerName: getInvestmentScoreHeader(
      t,
      "components.Reports.MeetingsDataTable.meetingCostIndex",
      isCostIndexPresentation
    ),
    headerComponent: GridHeader,
    cellClassRules: getHighlightingClasses("costValue"),
    valueGetter: (v) => getCostIndexGetter(v, isCostIndexPresentation),
    tooltipValueGetter: (v) => getCostIndexGetter(v, isCostIndexPresentation),
    valueFormatter: (v) => getCostIndexGetter(v, isCostIndexPresentation),
    cellRenderer: ({ value }) =>
      renderIndexValue(value, isCostIndexPresentation),
  },
];
